<template>
    <div v-if="$auth.auth && $auth.hasRole('super-admin')">
        <div class="row pt-3">
            <!-- Breadcrumbs -->
            <div class="col-md-8">
                <nav aria-label="breadcrumb">
                    <ol class="py-1 my-2 breadcrumb align-middle">
                        <li class="breadcrumb-item">
                            <a :href="'/networks/' + network.slug + '/meetings'" class="text-primary fw-bold">
                                {{ $t('network.tabMeetings') }}
                            </a>
                        </li>
                        <li class="breadcrumb-item">
                            <a class="text-secondary fw-bold" style="pointer-events: none;">
                                {{ $t('network.attendance-report-for') }} {{ meeting.title }} ({{ utcToLocal(meeting.start).format('MMM DD, YYYY - hh:mm A') }})
                            </a>
                        </li>
                    </ol>
                </nav>
            </div>
            <div class="col-md-4" style="text-align: right;">
                <router-link v-if="occurrence" :to="{ path: '/networks/' + network.slug + '/meetings/zoom-attendees/'+ meeting.id + '?occurrence=' + occurrence,
                    params: { network: network } }" class="btn btn-outline-primary btn-sm pt-2 mx-3" style="font-size: 15px; border: 2px solid;">
                    <i class="fas fa-list-ul me-2"></i>{{ $t('see-zoom-attendees') }}
                </router-link>
                <router-link v-else :to="{ path: '/networks/' + network.slug + '/meetings/zoom-attendees/'+ meeting.id, params: { network: network } }"
                class="btn btn-outline-primary btn-sm pt-2 mx-3" style="font-size: 15px; border: 2px solid;">
                    <i class="fas fa-list-ul me-2"></i>{{ $t('see-zoom-attendees') }}
                </router-link>
                <JsonCSV
                    :data="filteredAttendanceMeetings"
                    :name="meeting.title + '-' + start + '-attendees-list.csv'"
                    class="btn btn-primary btn-sm pt-2"
                >
                    <i class="fas fa-file-download me-2" style="font-size: 18px;"></i>{{ $t('export-csv') }}
                </JsonCSV>
            </div>
        </div>
        <div class="row">
            <div class="col-md-8">
                <div class="row pt-1">
                    <span style="font-weight: 700; padding-bottom: 10px;">{{ $t('search-for-users-to-add-as-attendees-to-this-meeting') }}</span>
                    <div class="col-md-6">
                        <input id="search_user_query" v-model="searchUserQuery" data-test="search_user_query" type="search" placeholder="Start typing to add users" class="form-control border-2 border-primary">
                    </div>
                    <UserSearch
                        :searchUserQuery="searchUserQuery"
                        :searched_users="searched_users"
                        :meeting_id="meeting.id"
                        :network_id="network.id"
                        :occurrence="occurrence"
                        @add-new-user="addNewUser"
                        @reload-meeting-list="refreshAllMeetings"
                    />
                    <label v-if="!searchedUserExists" class="pt-3 fw-bold small" for="">
                        <span class="text-danger">Oops!</span>
                        <span class="text-info">
                            {{ $t('it-seems-like-the-following-user-is-not-registered-with-us-add-them-by-clicking-the-button-below-to-create-an-unregistered-account-for-them') }}
                        </span>
                    </label>
                    <InviteUsers
                        :users="users"
                        @show-add-user-modal="m => (this.showNewUserForm = m)"
                    />
                </div>
            </div>
            <div class="col-md-4">
                <div class="search">
                    <input
                        id="search_attendee_query"
                        v-model="searchAttendeeQuery"
                        data-test="search_attendee_query"
                        type="text"
                        placeholder="Start typing to search users"
                        class="search-input"
                        @input="searchAttendees"
                    />
                    <i class="fa fa-search search-icon"></i>
                </div>
            </div>
        </div>
        <div v-if="!busy" class="table-container">
            <table class="table attendees">
                <thead>
                    <tr>
                        <th scope="col">{{ $t('network.auth.firstName') }}</th>
                        <th scope="col">{{ $t('network.auth.lastName') }}</th>
                        <th scope="col">{{ $t('network.auth.email') }}</th>
                        <th scope="col">{{ $t('network.auth.workplace.job_title') }}</th>
                        <th scope="col">{{ $t('network.auth.work-place') }}</th>
                        <th scope="col">{{ $t('network.auth.workplace_city') }}</th>
                        <th scope="col">{{ $t('network.auth.workplace_country') }}</th>
                        <th scope="col">{{ $t('network.auth.firstName') }} ({{ $t('curated') }})</th>
                        <th scope="col">Preferred First Name ({{ $t('curated') }})</th>
                        <th scope="col">Middle Name ({{ $t('curated') }})</th>
                        <th scope="col">{{ $t('network.auth.lastName') }} ({{ $t('curated') }})</th>
                        <th scope="col">Full Name ({{ $t('curated') }})</th>
                        <th scope="col">Preferred Full Name ({{ $t('curated') }})</th>
                        <th scope="col">{{ $t('network.auth.email') }} 1 ({{ $t('curated') }})</th>
                        <th scope="col">{{ $t('network.auth.email') }} 2 ({{ $t('curated') }})</th>
                        <th scope="col">{{ $t('network.auth.workplace.job_title') }} 1 ({{ $t('curated') }})</th>
                        <th scope="col">Department 1 ({{ $t('curated') }})</th>
                        <th scope="col">Hospital 1 ({{ $t('curated') }})</th>
                        <th scope="col">{{ $t('network.auth.workplace.job_title') }} 2 ({{ $t('curated') }})</th>
                        <th scope="col">Department 2 ({{ $t('curated') }})</th>
                        <th scope="col">Hospital 2 ({{ $t('curated') }})</th>
                        <th scope="col">{{ $t('network.auth.work-place') }} ({{ $t('curated') }})</th>
                        <th scope="col">{{ $t('network.auth.workplace_city') }} ({{ $t('curated') }})</th>
                        <th scope="col">{{ $t('network.auth.workplace_country') }} ({{ $t('curated') }})</th>
                        <th scope="col">Profile Link ({{ $t('curated') }})</th>
                        <th scope="col">Tags / Keywords</th>
                    </tr>
                </thead>
                <tbody>
                    <!-- Attendance represents a single tableRow or <tr> -->
                    <Attendance
                        v-for="(meeting, index) in filteredAttendanceMeetings"
                        :key="index"
                        :network="network"
                        :meeting="meeting"
                        :canStart="true"
                        :currentMeetingView="currentMeetingView"
                        @reload-meeting-list="refreshAllMeetings"
                    />

                    <tr v-if="filteredAttendanceMeetings.length == 0 && !busy">
                        <td colspan="26">
                            <div class="alert alert-info mt-2 text-center" style="position: unset;">
                                {{ $t('network.member.noResultsToShow') }}
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div v-if="busy" class="text-center fixed-centered">
            <lottie-player
                autoplay
                loop
	            speed="2.5"
                src="/img/animations/custom_loader.json"
                style="height: 130px;"
                >
            </lottie-player>
        </div>
        <!--Modal-->
        <b-modal v-model="showNewUserForm" hide-footer hide-header size="lg">
            <form @submit.prevent="submitNewUserForm">
                <div class="mb-2 ms-2">
                    {{ $t('please-enter-the-details-below-to-add-an-attendee-for-this-meeting-this-will-also-create-an-unregistered-account-for-them') }} </div>
                <div class="row py-1">
                    <div class="col-md-6">
                        <label for="first_name" class="form-label p-0">{{ $t('network.auth.firstName') }} <sup class="text-danger ms-1">*</sup></label>
                        <input id="first_name" v-model="form.first_name" data-test="first_name" type="text" placeholder="John" class="form-control" required>
                    </div>
                    <div class="col-md-6">
                        <label for="last_name" class="form-label p-0">{{ $t('network.auth.lastName') }}<sup class="text-danger ms-1">*</sup></label>
                        <input id="last_name" v-model="form.last_name" data-test="last_name" type="text" placeholder="Doe" class="form-control" required>
                    </div>
                </div>
                <div class="row py-1">
                    <div class="col-md-12">
                        <label for="email" class="form-label p-0">{{ $t('network.auth.email') }} <sup class="text-danger ms-1">*</sup></label>
                        <input id="email" v-model="form.email" data-test="email" type="email" placeholder="email@gmail.com" class="form-control" required>
                    </div>
                </div>
                <div class="row py-1">
                    <div class="col-md-6">
                        <label for="job_title" class="form-label p-0">{{ $t('network.auth.workplace.job_title') }}<sup class="text-danger ms-1">*</sup></label>
                        <input id="job_title" v-model="form.job_title" data-test="job_title" type="text" placeholder="Oncologist / Resident" class="form-control" required>
                    </div>
                    <div class="col-md-6">
                        <label for="workplace" class="form-label p-0">{{ $t('network.auth.work-place') }}<sup class="text-danger ms-1">*</sup></label>
                        <input id="workplace" v-model="form.workplace" data-test="workplace" type="text" placeholder="General Hospital Inc." class="form-control" required>
                    </div>
                </div>
                <div class="text-center">
                    <button id="submit" data-test="submit" type="submit" class="btn btn-primary btn mt-4">{{ $t('submit') }}</button>
                </div>
            </form>
        </b-modal>
    </div>
</template>
<script>
import Attendance from '@/components/commons/Meetings/Attendance.vue';
import InviteUsers from '../../../components/forms/InviteUsers.vue';
import UserSearch from '@/components/commons/UserSearch.vue';
import debounce from 'lodash/debounce';
import infiniteScroll from 'vue-infinite-scroll';
import JsonCSV from 'vue-json-csv';
import moment from 'moment';

export default {
    components: { 
        Attendance,
        UserSearch,
        InviteUsers,
        JsonCSV,
    },
    directives: { infiniteScroll },
    data() {
        return {
            meeting: [],
            network: [],
            occurrence: this.$route.query.occurrence ?? '',
            filteredAttendanceMeetings: [],
            attendees: [],
            currentMeetingView: 'past',
            start: null,
            busy: false,
            meeting_detail: {
                total_participant: 0,
                participant_details: [],
                unique_participant_names: [],
            },
            users: [],
            searched_users: [],
            searchUserQuery: '',
            searchedUserExists : true,
            form: {
                first_name: '',
                last_name: '',
                email: '',
                job_title: '',
                workplace: '',
            },
            showNewUserForm: false,
            searchAttendeeQuery: '',
        };
    },
    watch: {
        searchUserQuery: debounce(function(value) {
            this.searchUsers(value);
        },600),
        searchAttendeeQuery: debounce(function(value) {
            let search = document.getElementsByClassName('search')[0];
            let searchInput = document.getElementsByClassName('search-input')[0];
            let searchIcon = document.getElementsByClassName('search-icon')[0];
            if (this.searchAttendeeQuery != ''){
                search.style.width = '300px';
                searchInput.style.display = 'block';
                searchIcon.style.background = '#005f9a';
                searchIcon.style.color = 'white';
            } else {
                search.style.width = '50px';
                searchInput.style.display = 'none';
                searchIcon.style.background = 'unset';
                searchIcon.style.color = '#07051a';
            }
            this.searchAttendees(value);
        },600),
    },
    mounted() {
        this.load();
        if (this.$auth.auth){
            if (!this.$auth.hasRole('super-admin')) {
                this.$swal.fire({
                    icon: 'error',
                    title: this.$t('unauthorized-access'),
                }).then(() => {
                    this.$router.push('/networks/' + this.network.slug);
                });
            }
        } else {
            this.$bus.emit('show-sign-in');
        }
    },
    methods: {
        utcToLocal: function (dt) {
            return moment.utc(dt).local();
        },
        refreshAllMeetings() {
            this.searchUserQuery = '';
            this.filteredAttendanceMeetings.splice(0);
            this.attendees.splice(0);
            this.load();
        },
        load() {
            this.busy = true;
            let url = 'networks/meetings/' + this.$route.params.id;
            if (this.occurrence) url += '?occurrence=' + this.occurrence;
            this.$http.get(url).then(resp => {
                this.meeting = resp.data;
                this.network = resp.data.network;
                this.start = moment(this.meeting.start).format('YYYY-MM-DD');
                this.filteredAttendance();
            });
        },
        filteredAttendance(){
            this.filteredAttendanceMeetings.splice(0);
            this.attendees.splice(0);
            this.$http.get('networks/' + this.network.slug + '/meetings').then(resp => {
                [...resp.data.pastWithOccurrences, ...resp.data.inProgressWithOccurrences, ...resp.data.upcomingWithOccurrences].forEach(element => {
                    if(((moment(element.start).format('YYYY-MM-DD')) == (moment(this.start).format('YYYY-MM-DD')))){
                        this.$http.post('attendance/' + element.start).then(resp => {
                            if (resp.data != "") {
                                resp.data.forEach(element => {
                                    this.filteredAttendanceMeetings.push(element);
                                    this.attendees.push(element);
                                });
                            }
                        })
                        .then(() => {
                            this.busy = false;
                        });
                    }
                });
            });
        },
        addNewUser(searchedValue) {
            this.users = [];
            if(!searchedValue.user_id){
                let user = {
                    text : this.searchUserQuery
                };
                (!find(this.users,['text', user.text])) ? this.users.push(user) : '';
                this.searchUserQuery = '';
                //this.showNewUserForm = true;
            }
        },
        searchUsers(searchTerm){
            this.searched_users = [];
            if(searchTerm.length > 2){
                this.axios.get('users?limit=5' + '&query=' + searchTerm).then((resp) => {
                    if(resp.data.length != 0){
                        this.searched_users = resp.data;
                        this.searchedUserExists = true;
                    } else {
                        this.searchedUserExists = false;
                        this.addNewUser(searchTerm);
                    }
                })
                .catch(err => {
                    console.log('err=', err);
                });
            }
        },
        submitNewUserForm() {
            let url = 'join-meeting/' + this.$route.params.id;
            url += this.occurrence != '' ? '/' + this.occurrence + '/' + null : '/' + null + '/' + null;
            this.$swal.fire({
                icon: 'info',
                title: this.$t('do-you-really-want-to-add-this-user-as-an-attendee'),
                showCancelButton: true,
                confirmButtonText : this.$t('ResearchCreds.yes'),
                customClass: {
                    confirmButton: 'btn btn-success mx-1',
                    cancelButton: 'btn btn-primary mx-1',
                },
                buttonsStyling: false,
            }).then( (result) => {
                if(result.isConfirmed){
                    this.form.newUser = true;
                    this.$http.post(url, this.form).then(() => {
                        this.showNewUserForm = false;
                        this.$swal.fire({
                            icon: 'success',
                            title: this.$t('success'),
                        }).then(() => {
                            // removes search query badge and refreshes all meetings
                            this.users = [];
                            this.searchedUserExists = true;
                            this.refreshAllMeetings();
                        });
                    });
                }
            });
        },
        searchAttendees(searchTerm){
            this.filteredAttendanceMeetings.splice(0);
            if(searchTerm.length == 0){
                this.filteredAttendance();
            } else {
                this.$http.get('meetings/'+ this.network.slug +'/'+ this.$route.params.id +'/'+ this.occurrence +'/search-attendee?query=' + searchTerm).then((resp) => {
                    if(resp.data != []){
                        resp.data.forEach(element => {
                            this.attendees.forEach(e => {
                                if (element == e.user_id) {
                                    this.filteredAttendanceMeetings.push(e);
                                }
                            });
                        });
                    }
                });
            }
        },
    },
};
</script>
<style scoped>
th {
  width: 150px;
  text-align: center;
  color: #5C6D80;
  font-weight: 600;
  font-size: 19px;
  line-height: 30px;
}
.attendees th {
  font-size: 17px;
  line-height: unset;
}
.text-white:hover {
    text-decoration: none;
}
.search{
  position: relative;
  top: 50%;
  left: 100%;
  transform: translate(-100%,-40%);
  transition: all 1s;
  width: 50px;
  height: 47px;
  background: rgba(0, 0, 0, 0.085);
  box-sizing: border-box;
  border-radius: 25px;
  border: 2px solid rgba(0, 0, 0, 0.085);
  padding: 5px;
  float: left;
  margin: unset;
  cursor: pointer;
}
.search-input{
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;;
  height: 42.5px;
  line-height: 30px;
  outline: 0;
  border: 0;
  display: none;
  font-size: 1em;
  border-radius: 20px;
  padding: 0 20px;
}
.search-icon{
  box-sizing: border-box;
  padding: 13px 2px 14px 0;
  width: 42.5px;
  height: 42.5px;
  position: absolute;
  top: 0;
  right: 0;
  border-radius: 50%;
  color: #07051a;
  text-align: center;
  font-size: 1.3em;
  transition: all 1s;
}
.search:hover{
  width: 300px !important;
}
.search:hover .search-input{
  display: block !important;
}
.search:hover .search-icon{
  background: #005f9a !important;
  color: white !important;
}
.table-container {
    overflow-x: auto;
    width: 100%;
}
</style>